import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

export default function GradientBar({
  values,
  labels,
  min = 0,
  max,
  isExport = false,
}) {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (values.length === 0) {
    return (
      <Box>
        <Typography variant="body1" color="error">
          At least one value is required to render the gradient bar.
        </Typography>
      </Box>
    );
  }

  // Use the provided max value or calculate it if not provided
  const effectiveMin = min || 0;
  const effectiveMax = max || Math.max(...values);

  // Calculate mean and median
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const sortedValues = [...values].sort((a, b) => a - b);
  const median =
    sortedValues.length % 2 === 0
      ? (sortedValues[sortedValues.length / 2 - 1] +
          sortedValues[sortedValues.length / 2]) /
        2
      : sortedValues[Math.floor(sortedValues.length / 2)];

  // Function to calculate percentage position
  const getPercentage = (value) =>
    ((value - effectiveMin) / (effectiveMax - effectiveMin)) * 100;

  return (
    <Box sx={{ width: "100%", mt: 2, position: "relative" }}>
      {/* Gradient Bar */}
      <Box
        sx={{
          width: "100%",
          height: "30px",
          background: "linear-gradient(to right, green, yellow, red)",
          borderRadius: "15px",
          position: "relative",
        }}
      >
        {/* Render Vertical Lines */}
        {values.map((value, index) => {
          const percentage = getPercentage(value);
          const lineTop = -10;
          const lineHeight = 60;

          return (
            <Box
              key={index}
              onMouseEnter={() => !isExport && setHoveredIndex(index)}
              onMouseLeave={() => !isExport && setHoveredIndex(null)}
              sx={{
                position: "absolute",
                left: `${percentage}%`,
                top: `${lineTop}px`,
                transform: "translate(-50%, 0)",
                width: { xs: "5px", sm: "8px", xl: "8px" },
                height: `${lineHeight}px`,
                backgroundColor: "black",
                cursor: "default",
                marginRight: "20px",
              }}
            />
          );
        })}

        {/* Render Labels */}
        {!isExport &&
          values.map((value, index) => {
            if (hoveredIndex !== index) return null;

            const percentage = getPercentage(value);
            const labelName = labels?.[index] || `Value ${index + 1}`;
            const lineTop = -10;
            const lineHeight = 60;
            const isTop = index % 2 === 0;
            const labelTop = isTop ? lineTop - 30 : lineTop + lineHeight + 10;

            return (
              <Typography
                key={index}
                variant="caption"
                sx={{
                  position: "absolute",
                  left: `${percentage}%`,
                  top: `${labelTop}px`,
                  transform: "translateX(-100%)",
                  fontWeight: "bold",
                  color: "black",
                  padding: "5px",
                  whiteSpace: "nowrap",
                  textAlign: "right",
                  backgroundColor: "white",
                  borderRadius: "4px",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                }}
              >
                {labelName}: {value.toLocaleString() / 1000} k$
              </Typography>
            );
          })}

        {/* Show mean and median only in export mode */}
        {isExport && (
          <>
            {/* Mean */}

            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                left: `${getPercentage(mean) - 8}%`,
                top: "-40px",
                transform: "translateX(-100%)",
                fontWeight: "bold",
                color: "black",
                padding: "5px",
                whiteSpace: "nowrap",
                textAlign: "right",
                backgroundColor: "white",
                borderRadius: "4px",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              Mean: {(mean / 1000).toFixed(0)} k$
            </Typography>

            {/* Median */}

            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                left: `${getPercentage(median)}%`,
                top: "50px",
                transform: "translateX(-100%)",
                fontWeight: "bold",
                color: "black",
                padding: "5px",
                whiteSpace: "nowrap",
                textAlign: "right",
                backgroundColor: "white",
                borderRadius: "4px",
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              Median: {(median / 1000).toFixed(0)} k$
            </Typography>
          </>
        )}
      </Box>

      {/* Min and Max Labels */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Typography sx={{ fontWeight: "bold" }}>{effectiveMin}</Typography>
        <Typography sx={{ fontWeight: "bold" }}>
          {(effectiveMax / 1000).toFixed(0)}K$
        </Typography>
      </Box>
    </Box>
  );
}
